import React, { useEffect, useState, useContext } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { withSnackbar, ProviderContext } from 'notistack';
import { ApplicationContext, AppContext } from '../../context/Contexts';
import { ITranslatorService } from '../../services/Interfaces/ITranslatorService';
import { LegalDocumentService } from '../../services/LegalDocumentService';
import { LegalDocumentEnum } from '../../interfaces/LegalDocument';
import useConnectAppTypeId from '../../hooks/useConnectAppTypeId';
import PdfViewer from '../pdf-viewer/PdfViewer';
import Logo from '../_shared/Logo';

interface LegalDocumentPageProps {
  documentType: LegalDocumentEnum;
  title: string;
  translationKey: string;
}

type Props = LegalDocumentPageProps & ProviderContext;

const HEADER_HEIGHT = 64;

const LegalDocumentPage: React.FC<Props> = ({
  documentType,
  title,
  translationKey,
  enqueueSnackbar
}) => {
  const context = useContext(ApplicationContext);
  const translatorService: ITranslatorService = (context as AppContext).translatorService;
  const legalDocumentService: LegalDocumentService = (context as AppContext).legalDocumentService;

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const connectAppTypeId = useConnectAppTypeId();

  useEffect(() => {
    if (!connectAppTypeId) return;

    let revokableUrl: string | null = null;

    const loadDocument = async () => {
      try {
        setIsLoading(true);
        setError(false);

        const blob = await legalDocumentService.GetLastLegalDocumentVersionByType(
          documentType,
          connectAppTypeId
        );

        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(pdfBlob);
        revokableUrl = blobUrl;
        setPdfUrl(blobUrl);
      } catch (err) {
        console.error('Error loading document:', err);
        setError(true);
        enqueueSnackbar(
          translatorService.Tranlate('FLEET_DOCUMENT_FAILED_TO_LOAD', 'Failed to load document'),
          { variant: 'error' }
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadDocument();

    return () => {
      if (revokableUrl) {
        URL.revokeObjectURL(revokableUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentType, connectAppTypeId]);

  return (
    <Box style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        style={{
          height: HEADER_HEIGHT,
          minHeight: HEADER_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e0e0e0',
          position: 'relative'
        }}
      >
        <Logo height={56} />
        <Typography variant="h5" style={{ width: '100%', textAlign: 'center', fontWeight: 600 }}>
          {translatorService.Tranlate(translationKey, title)}
        </Typography>
      </Box>

      {isLoading && (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}

      {error && !isLoading && (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1,
            flexDirection: 'column'
          }}
        >
          <Typography variant="h5" color="error">
            {translatorService.Tranlate('FLEET_DOCUMENT_FAILED_TO_LOAD', 'Failed to load document')}
          </Typography>
          <Typography variant="body1" style={{ marginTop: 8, color: 'rgba(0,0,0,0.54)' }}>
            {translatorService.Tranlate(
              'FLEET_DOCUMENT_LOAD_ERROR_MESSAGE',
              'Please try again later or contact support.'
            )}
          </Typography>
        </Box>
      )}

      {!isLoading && !error && pdfUrl && (
        <Box style={{ flexGrow: 1, overflow: 'hidden' }}>
          <PdfViewer pdfUrl={pdfUrl} height="100%" title={title} />
        </Box>
      )}
    </Box>
  );
};

export default withSnackbar(LegalDocumentPage as any);
